import { useFrame } from '@react-three/fiber';
import { useMemo, useRef, useState } from 'react';
import { Matrix4, Quaternion, type Camera } from 'three';

import { getScreenDirections } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';

import type {
    EdgeHandle,
    PanelReplacementDirection
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';

export const useReplacementScreenDirectionsHandler = (
    enabled: boolean,
    camera: Camera,
    edgeHandles: EdgeHandle[],
    activeEdge: EdgeHandle | null
) => {
    const [screenDirections, setScreenDirections] = useState<Record<string, PanelReplacementDirection>>({});
    const [viewRevision, setViewRevision] = useState(0);
    const viewQuaternionRef = useRef(new Quaternion());
    const cameraWorldMatrixRef = useRef(new Matrix4());
    const cameraProjectionMatrixRef = useRef(new Matrix4());
    const cameraMatricesInitializedRef = useRef(false);
    const screenDirectionHandlesRef = useRef<EdgeHandle[] | null>(null);
    const screenDirectionsRef = useRef<Record<string, PanelReplacementDirection>>({});

    useFrame(() => {
        if (!enabled) {
            return;
        }
        const cameraChanged =
            screenDirectionHandlesRef.current !== edgeHandles ||
            !cameraMatricesInitializedRef.current ||
            !camera.matrixWorld.equals(cameraWorldMatrixRef.current) ||
            !camera.projectionMatrix.equals(cameraProjectionMatrixRef.current);
        if (!cameraChanged) {
            return;
        }

        cameraWorldMatrixRef.current.copy(camera.matrixWorld);
        cameraProjectionMatrixRef.current.copy(camera.projectionMatrix);
        cameraMatricesInitializedRef.current = true;
        screenDirectionHandlesRef.current = edgeHandles;
        if (activeEdge && !viewQuaternionRef.current.equals(camera.quaternion)) {
            viewQuaternionRef.current.copy(camera.quaternion);
            setViewRevision((revision) => revision + 1);
        }
        const nextDirections = getScreenDirections(edgeHandles, camera);
        const previousDirections = screenDirectionsRef.current;
        const changed =
            Object.keys(nextDirections).length !== Object.keys(previousDirections).length ||
            Object.entries(nextDirections).some(([key, direction]) => previousDirections[key] !== direction);
        if (changed) {
            screenDirectionsRef.current = nextDirections;
            setScreenDirections(nextDirections);
        }
    });

    const currentScreenDirections = useMemo(
        () =>
            Object.keys(screenDirections).length > 0
                ? screenDirections
                : getScreenDirections(edgeHandles, camera),
        [camera, edgeHandles, screenDirections]
    );

    return { currentScreenDirections, viewRevision };
};
